import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { StructureTypeCreateDTO } from '../../../types/fields';

export interface StructureImportDialogResult {
  action: 'import' | 'cancel';
  structures?: StructureTypeCreateDTO[];
}

@Component({
  selector: 'app-structure-import-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/30">
            <mat-icon class="text-green-600">upload</mat-icon>
          </div>
          <div>
            <h2 data-testid="structure-import-dialog-heading" class="text-lg font-semibold text-zinc-900 dark:text-white">Import Structures</h2>
            <p class="text-sm text-zinc-500 dark:text-zinc-400">Paste JSON to import structures</p>
          </div>
        </div>
        <button
          mat-icon-button
          (click)="onCancel()"
          class="!-mr-2 !-mt-2"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- JSON Input -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-zinc-700 dark:text-zinc-300">JSON Data</label>
        <textarea
          data-testid="structure-import-dialog-json-input"
          [(ngModel)]="jsonInput"
          (ngModelChange)="validateJson()"
          placeholder='[
  {
    "id": "optional-uuid",
    "name": "Structure Name",
    "description": "Structure description",
    "fieldIds": ["field-id-1", "field-id-2"],
    "color": "#3B82F6"
  }
]'
          class="w-full h-64 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-2 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none dark:placeholder:text-zinc-500"
        ></textarea>

        @if (errorMessage()) {
          <div class="flex items-center gap-2 text-red-600 text-sm">
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">error</mat-icon>
            <span>{{ errorMessage() }}</span>
          </div>
        }
        @if (parsedStructures().length > 0 && !errorMessage()) {
          <div class="flex items-center gap-2 text-green-600 text-sm">
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">check_circle</mat-icon>
            <span>{{ parsedStructures().length }} structure(s) ready to import</span>
          </div>
        }
      </div>

      <!-- Paste from Clipboard Button -->
      <div class="mt-4">
        <button
          data-testid="structure-import-dialog-paste-btn"
          mat-stroked-button
          (click)="pasteFromClipboard()"
          class="w-full"
        >
          <mat-icon>content_paste</mat-icon>
          <span class="ml-2">Paste from Clipboard</span>
        </button>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-700">
        <button data-testid="structure-import-dialog-cancel-btn" mat-stroked-button (click)="onCancel()">
          Cancel
        </button>
        <button
          data-testid="structure-import-dialog-import-btn"
          mat-flat-button
          color="primary"
          [disabled]="parsedStructures().length === 0 || !!errorMessage()"
          (click)="onImport()"
        >
          <mat-icon>upload</mat-icon>
          <span class="ml-1">Import {{ parsedStructures().length }} Structure(s)</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class StructureImportDialogComponent {
  private dialogRef = inject(MatDialogRef<StructureImportDialogComponent>);

  jsonInput = '';
  errorMessage = signal<string>('');
  parsedStructures = signal<StructureTypeCreateDTO[]>([]);

  validateJson(): void {
    if (!this.jsonInput.trim()) {
      this.errorMessage.set('');
      this.parsedStructures.set([]);
      return;
    }

    try {
      const parsed = JSON.parse(this.jsonInput);
      const structures = Array.isArray(parsed) ? parsed : [parsed];

      const validStructures: StructureTypeCreateDTO[] = [];
      for (let i = 0; i < structures.length; i++) {
        const structure = structures[i];
        const validation = this.validateStructure(structure, i);
        if (validation.error) {
          this.errorMessage.set(validation.error);
          this.parsedStructures.set([]);
          return;
        }
        validStructures.push(validation.structure!);
      }

      this.errorMessage.set('');
      this.parsedStructures.set(validStructures);
    } catch (e) {
      this.errorMessage.set('Invalid JSON format');
      this.parsedStructures.set([]);
    }
  }

  private validateStructure(structure: any, index: number): { error?: string; structure?: StructureTypeCreateDTO } {
    const prefix = `Structure ${index + 1}: `;

    if (!structure || typeof structure !== 'object') {
      return { error: `${prefix}Must be an object` };
    }
    if (!structure.name || typeof structure.name !== 'string') {
      return { error: `${prefix}Missing or invalid 'name' (string required)` };
    }
    if (!Array.isArray(structure.fieldIds)) {
      return { error: `${prefix}Missing or invalid 'fieldIds' (array required)` };
    }
    if (!structure.color || typeof structure.color !== 'string') {
      return { error: `${prefix}Missing or invalid 'color' (string required)` };
    }

    const validStructure: StructureTypeCreateDTO = {
      ...(structure.id && typeof structure.id === 'string' ? { id: structure.id } : {}),
      name: structure.name.trim(),
      description: structure.description || '',
      fieldIds: structure.fieldIds,
      color: structure.color
    };

    return { structure: validStructure };
  }

  async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.jsonInput = text;
      this.validateJson();
    } catch (e) {
      this.errorMessage.set('Unable to read from clipboard. Please paste manually.');
    }
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as StructureImportDialogResult);
  }

  onImport(): void {
    if (this.parsedStructures().length > 0) {
      this.dialogRef.close({
        action: 'import',
        structures: this.parsedStructures()
      } as StructureImportDialogResult);
    }
  }
}
